import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, matchPath } from 'react-router-dom';

import { useAuth, User } from '../context/AuthContext';
import { useSkillStoreVisibility } from '../hooks/useSkillStoreVisibility';
import { hasEngineerPrivileges, isSupportStaff, isSystemAdmin } from '../utils/systemRoles';

import { CreditDisplay, CreditWarningToastListener } from './CreditDisplay';
import { ProfileAvatar } from './ProfileAvatar';

interface UserMenuProps {
  user: User;
}

interface MenuItemProps {
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  className?: string;
  disabled?: boolean;
}

const ProfileIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="user-menu__icon"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const ProjectsIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="user-menu__icon"
  >
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
  </svg>
);

const BillingIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="user-menu__icon"
  >
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);

const SkillStoreIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="user-menu__icon"
  >
    <path d="M8.5 3H5a2 2 0 0 0-2 2v3.5a2.5 2.5 0 1 1 0 5V19a2 2 0 0 0 2 2h3.5a2.5 2.5 0 1 1 5 0H19a2 2 0 0 0 2-2v-5.5a2.5 2.5 0 1 1 0-5V5a2 2 0 0 0-2-2h-5.5a2.5 2.5 0 1 1-5 0Z" />
  </svg>
);

const APIIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="user-menu__icon"
  >
    <path d="M8 9l-3 3 3 3" />
    <path d="M16 9l3 3-3 3" />
    <path d="M14 5l-4 14" />
  </svg>
);

const InvoiceIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="user-menu__icon"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="8" y1="13" x2="16" y2="13" />
    <line x1="8" y1="17" x2="16" y2="17" />
    <line x1="8" y1="9" x2="10" y2="9" />
  </svg>
);

const CouponIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="user-menu__icon"
  >
    <path d="M20 12a2 2 0 0 0 0-4V5a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v3a2 2 0 0 0 0 4v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2z" />
    <line x1="12" y1="5" x2="12" y2="7" />
    <line x1="12" y1="10" x2="12" y2="12" />
    <line x1="12" y1="15" x2="12" y2="17" />
  </svg>
);

const TeamIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="user-menu__icon"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const ManageUsersIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="user-menu__icon"
  >
    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="8.5" cy="7" r="4" />
    <line x1="20" y1="8" x2="20" y2="14" />
    <line x1="23" y1="11" x2="17" y2="11" />
  </svg>
);

const AnalyticsIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="user-menu__icon"
  >
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6"  y1="20" x2="6"  y2="14" />
    <line x1="2"  y1="20" x2="22" y2="20" />
  </svg>
);

const ChevronIcon: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`user-menu__chevron ${open ? 'user-menu__chevron--open' : ''}`}
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const AgentGraphIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="user-menu__icon"
  >
    <rect x="2" y="3" width="6" height="5" rx="1.5" />
    <rect x="16" y="3" width="6" height="5" rx="1.5" />
    <rect x="9" y="16" width="6" height="5" rx="1.5" />
    <path d="M5 8v3.5a1.5 1.5 0 0 0 1.5 1.5h11A1.5 1.5 0 0 0 19 11.5V8" />
    <path d="M12 13v3" />
  </svg>
);

const SettingsIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="user-menu__icon"
  >
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const SupportIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="user-menu__icon"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const LogoutIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="user-menu__icon"
  >
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const MenuItem: React.FC<MenuItemProps> = ({
  onClick,
  icon,
  label,
  className = 'user-menu__item',
  disabled = false,
}) => (
  <button onClick={onClick} className={className} disabled={disabled}>
    {icon}
    {label}
  </button>
);

const UserInfo: React.FC<{ user: User }> = ({ user }) => (
  <div className="user-menu__info">
    <div className="user-menu__name">{user.displayName || user.email}</div>
    <div className="user-menu__email">{user.email}</div>
  </div>
);

interface AdminMenuItemsProps {
  onNavigate: (path: string) => void;
}

const ANALYTICS_LINKS: { path: string; label: string }[] = [
  { path: '/admin/performance-analytics', label: 'Performance' },
  { path: '/admin/traffic-analysis', label: 'Traffic Analysis' },
  { path: '/admin/model-analytics', label: 'Model Analytics' },
  { path: '/admin/intent-analytics', label: 'Intent Analytics' },
  { path: '/admin/quality-analytics?metric=seriousness', label: 'User Seriousness' },
  { path: '/admin/quality-analytics?metric=satisfaction', label: 'User Satisfaction' },
];

const AnalyticsMenuItems: React.FC<{ onNavigate: (path: string) => void }> = ({ onNavigate }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="user-menu__group">
      <button
        type="button"
        className="user-menu__item user-menu__item--parent"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
      >
        <AnalyticsIcon />
        <span>Analytics</span>
        <ChevronIcon open={isOpen} />
      </button>
      {isOpen && (
        <div className="user-menu__submenu">
          {ANALYTICS_LINKS.map((link) => (
            <button key={link.path} type="button" className="user-menu__subitem" onClick={() => onNavigate(link.path)}>
              {link.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const AdminMenuItems: React.FC<AdminMenuItemsProps & {
  isSystemAdmin: boolean;
  skillStoreVisible: boolean;
}> = ({ onNavigate, isSystemAdmin, skillStoreVisible }) => (
  <>
    <div className="user-menu__divider" />
    <MenuItem onClick={() => onNavigate('/admin/users')} icon={<ManageUsersIcon />} label="Manage Users" />
    <MenuItem onClick={() => onNavigate('/admin/projects')} icon={<ProjectsIcon />} label="Manage Projects" />
    <AnalyticsMenuItems onNavigate={onNavigate} />
    <SupportMenuItems onNavigate={onNavigate} />
    {isSystemAdmin && (
      <>
        <MenuItem onClick={() => onNavigate('/admin/coupons')} icon={<CouponIcon />} label="Signup Coupons" />
        <MenuItem
          onClick={() => onNavigate('/admin/monthly-invoices')}
          icon={<InvoiceIcon />}
          label="Monthly Invoices"
        />
        {skillStoreVisible && (
          <MenuItem onClick={() => onNavigate('/admin/cashouts')} icon={<BillingIcon />} label="Cashout Requests" />
        )}
        <MenuItem
          onClick={() => onNavigate('/admin/agent-manager')}
          icon={<AgentGraphIcon />}
          label="Agent Manager"
        />
        <MenuItem onClick={() => onNavigate('/admin/models')} icon={<APIIcon />} label="Model Settings" />
        <MenuItem onClick={() => onNavigate('/admin/model-logs')} icon={<InvoiceIcon />} label="Model Logs" />
        <MenuItem onClick={() => onNavigate('/admin/settings')} icon={<SettingsIcon />} label="Admin Settings" />
      </>
    )}
  </>
);

const SupportMenuItems: React.FC<{ onNavigate: (path: string) => void }> = ({ onNavigate }) => (
  <>
    <MenuItem onClick={() => onNavigate('/support/dashboard')} icon={<SupportIcon />} label="Support" />
    {/* Future support-specific items can be added here easily */}
  </>
);

const useClickOutside = (ref: React.RefObject<HTMLDivElement | null>, callback: () => void) => {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callback();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [ref, callback]);
};

const useEscapeKey = (callback: () => void) => {
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        callback();
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => document.removeEventListener('keydown', handleEscape);
  }, [callback]);
};

const useProjectId = () => {
  const location = useLocation();
  const projectMatch =
    matchPath({ path: '/planning/:projectId' }, location.pathname) ||
    matchPath({ path: '/projects/:projectId/*' }, location.pathname);

  return projectMatch?.params.projectId || new URLSearchParams(location.search).get('project') || undefined;
};

const getImageUrl = (user: User) => user.profileImageUrl || user.avatarUrl || null;

interface MenuDropdownProps {
  user: User;
  isLoading: boolean;
  onNavigate: (path: string) => void;
  onTeamNavigate: () => void;
  onLogout: () => void;
  projectId?: string;
}

interface MenuActionsProps {
  user: User;
  isLoading: boolean;
  onNavigate: (path: string) => void;
  onTeamNavigate: () => void;
  onLogout: () => void;
}

// eslint-disable-next-line max-lines-per-function
const MenuActions: React.FC<MenuActionsProps> = ({
  user,
  isLoading,
  onNavigate,
  onTeamNavigate,
  onLogout,
}) => {
  const userIsSystemAdmin = isSystemAdmin(user.system_role);
  const isAdminOrEngineer = hasEngineerPrivileges(user.system_role);
  const isStaff = isSupportStaff(user.system_role);
  const skillStoreVisible = useSkillStoreVisibility();

  return (
    <div className="user-menu__actions">
      <MenuItem onClick={() => onNavigate('/profile')} icon={<ProfileIcon />} label="Profile" />
      <MenuItem onClick={() => onNavigate('/projects')} icon={<ProjectsIcon />} label="My Projects" />
      {skillStoreVisible && (
        <MenuItem onClick={() => onNavigate('/skill-store')} icon={<SkillStoreIcon />} label="Skill Store" />
      )}
      <MenuItem onClick={() => onNavigate('/billing')} icon={<BillingIcon />} label="Billing" />
      <MenuItem onClick={() => onNavigate('/api-platform')} icon={<APIIcon />} label="API Platform" />
      <MenuItem onClick={() => onNavigate('/pricing')} icon={<BillingIcon />} label="Pricing & Plans" />
      <MenuItem onClick={onTeamNavigate} icon={<TeamIcon />} label="Team Members" />

      {!isStaff && <MenuItem onClick={() => onNavigate('/support')} icon={<SupportIcon />} label="Support" />}

      {isStaff && !isAdminOrEngineer && (
        <>
          <div className="user-menu__divider" />
          <SupportMenuItems onNavigate={onNavigate} />
        </>
      )}
      {isAdminOrEngineer && (
        <AdminMenuItems
          onNavigate={onNavigate}
          isSystemAdmin={userIsSystemAdmin}
          skillStoreVisible={skillStoreVisible}
        />
      )}
      <MenuItem
        onClick={onLogout}
        icon={<LogoutIcon />}
        label={isLoading ? 'Logging out...' : 'Log out'}
        className="user-menu__logout"
        disabled={isLoading}
      />
    </div>
  );
};

const MenuDropdown: React.FC<MenuDropdownProps> = ({
  user,
  isLoading,
  onNavigate,
  onTeamNavigate,
  onLogout,
  projectId,
}) => (
  <div className="user-menu__dropdown">
    <UserInfo user={user} />
    <div className="user-menu__credit-row">
      <CreditDisplay currentProjectId={projectId} />
    </div>
    <MenuActions
      user={user}
      isLoading={isLoading}
      onNavigate={onNavigate}
      onTeamNavigate={onTeamNavigate}
      onLogout={onLogout}
    />
  </div>
);

const useUserMenuHandlers = (
  navigate: ReturnType<typeof useNavigate>,
  logout: () => Promise<void>,
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>,
  projectId: string | undefined
) => {
  const handleLogout = async () => {
    try {
      await logout();
      setIsOpen(false);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setIsOpen(false);
  };

  const handleTeamNavigate = () => {
    const teamPath = projectId ? `/projects/${projectId}/team` : '/team';

    handleNavigate(teamPath);
  };

  return { handleLogout, handleNavigate, handleTeamNavigate };
};

interface ProfileButtonProps {
  onClick: () => void;
  isOpen: boolean;
  user: User;
}

const ProfileButton: React.FC<ProfileButtonProps> = ({ onClick, isOpen, user }) => (
  <button onClick={onClick} aria-expanded={isOpen} aria-haspopup="true" className="user-menu__button">
    <ProfileAvatar imageUrl={getImageUrl(user)} name={user.displayName || user.email} size="small" />
  </button>
);

const useUserMenuState = (menuRef: React.RefObject<HTMLDivElement | null>) => {
  const [isOpen, setIsOpen] = useState(false);
  const { logout, isLoading } = useAuth();

  useClickOutside(menuRef, () => setIsOpen(false));
  useEscapeKey(() => setIsOpen(false));

  return { isOpen, setIsOpen, logout, isLoading };
};

export const UserMenu: React.FC<UserMenuProps> = ({ user }) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const { isOpen, setIsOpen, logout, isLoading } = useUserMenuState(menuRef);
  const projectId = useProjectId();
  const handlers = useUserMenuHandlers(useNavigate(), logout, setIsOpen, projectId);

  return (
    <div ref={menuRef} className="user-menu">
      <ProfileButton onClick={() => setIsOpen(!isOpen)} isOpen={isOpen} user={user} />
      {isOpen && (
        <MenuDropdown
          user={user}
          isLoading={isLoading}
          onNavigate={handlers.handleNavigate}
          onTeamNavigate={handlers.handleTeamNavigate}
          onLogout={handlers.handleLogout}
          projectId={projectId}
        />
      )}
      <CreditWarningToastListener />
    </div>
  );
};
